import type {
  LAYOUT,
  REPORT,
  APEX_CLASS,
  APEX_TRIGGER,
  APPROVAL_PROCESS,
  EMAIL_TEMPLATE,
  RECORD_TYPE,
  WORKFLOW_RULE,
} from '@server/parser';
import { FIELD, FLOW, PROCESS_BUILDER_FLOW, VALIDATION_RULE } from '@server/parser';
import type { RelatedListProperties } from '@server/documentation';
import type { ReactNode } from 'react';
import type { NameProperties } from './ParserTypes';
import type { DocumentationItem } from '@server/documentation';

//sweep element types
export const SWEEP_AUTOMATION = 'SWEEP_AUTOMATION';
export const SWEEP_ALERT = 'SWEEP_ALERT';
export const SWEEP_ASSIGNMENT = 'SWEEP_ASSIGNMENT';
export const SWEEP_SCHEDULED_ASSIGNMENT = 'SWEEP_SCHEDULED_ASSIGNMENT';
export const SWEEP_DEDUPE = 'SWEEP_DEDUPE';
export const SWEEP_MATCHING = 'SWEEP_MATCHING';
export const ROLLUP = 'ROLLUP';

// Salesforce-specific base interface extending the shared DocumentationItem
export interface SalesforceItemBase extends DocumentationItem {
  objectApiNames: string[];

  description?: string;
  helpText?: string;
  referencedObjects?: string[];
  relatedObjects?: string[];
  link?: string;
  updatedAt?: string;
  createdAt?: string;
  updatedBy?: string;
  createdBy?: string;
  isActive?: boolean;
  namespace?: string;

  stagesNames?: string[];
  usedByRecordTypes?: string[];
  usedByPicklistValue?: string[];
}

export interface SalesforceItemField extends SalesforceItemBase {
  parentType: typeof FIELD;
  fieldType: string;
  isMandatory?: boolean;
  usage?: number;
  isRollup?: boolean;
  formula?: string;
}

export interface SalesforceItemLayout extends SalesforceItemBase {
  parentType: typeof LAYOUT;
  buttons?: NameProperties[];
  relatedLists?: RelatedListProperties[];
}

export interface SalesforceItemFlow extends SalesforceItemBase {
  parentType: typeof FLOW | typeof PROCESS_BUILDER_FLOW;
  flowType?: string;
  flowDescription?: string;
  processType?: string;
}

export interface SalesforceItemValidationRule extends SalesforceItemBase {
  parentType: typeof VALIDATION_RULE;
  errorMessage?: string;
  formula?: string;
}

export interface SalesforceItemApexTrigger extends SalesforceItemBase {
  parentType: typeof APEX_TRIGGER;
}

export interface SalesforceItemApexClass extends SalesforceItemBase {
  parentType: typeof APEX_CLASS;
}

export interface SalesforceItemApprovalProcess extends SalesforceItemBase {
  parentType: typeof APPROVAL_PROCESS;
}

export interface SalesforceItemWorkflowRule extends SalesforceItemBase {
  parentType: typeof WORKFLOW_RULE;
}

export interface ConfigurationItemRecordType extends SalesforceItemBase {
  parentType: typeof RECORD_TYPE;
}

export interface SalesforceItemReport extends SalesforceItemBase {
  parentType: typeof REPORT;
}

export interface SalesforceItemEmailTemplate extends SalesforceItemBase {
  parentType: typeof EMAIL_TEMPLATE;
}

export interface SalesforceItemRollup extends SalesforceItemBase {
  parentType: typeof ROLLUP;
  fieldType: string;
  isMandatory?: boolean;
  usage?: number;
  isRollup?: boolean;
  formula?: string;
}

interface SalesforceItemBaseSweepElement extends SalesforceItemBase {
  actions?: string[];
  trigger?: string;
  actionsToRender?: ReactNode[];
}

// Sweep element types
export interface SalesforceItemSweepAutomation extends SalesforceItemBaseSweepElement {
  parentType: typeof SWEEP_AUTOMATION;
}

export interface SalesforceItemSweepAlert extends SalesforceItemBaseSweepElement {
  parentType: typeof SWEEP_ALERT;
}

export interface SalesforceItemSweepAssignment extends SalesforceItemBaseSweepElement {
  parentType: typeof SWEEP_ASSIGNMENT;
}

export interface SalesforceItemSweepScheduledAssignment extends SalesforceItemBaseSweepElement {
  parentType: typeof SWEEP_SCHEDULED_ASSIGNMENT;
}

export interface SalesforceItemSweepDedupe extends SalesforceItemBaseSweepElement {
  parentType: typeof SWEEP_DEDUPE;
}

export interface SalesforceItemSweepMatching extends SalesforceItemBaseSweepElement {
  parentType: typeof SWEEP_MATCHING;
}

// Generic fallback for unclassified or other types
export interface SalesforceItemGeneric extends SalesforceItemBase {
  parentType: string;
}

export type SalesforceItem =
  | SalesforceItemField
  | SalesforceItemLayout
  | SalesforceItemFlow
  | SalesforceItemValidationRule
  | SalesforceItemApexTrigger
  | SalesforceItemApexClass
  | SalesforceItemApprovalProcess
  | SalesforceItemWorkflowRule
  | ConfigurationItemRecordType
  | SalesforceItemReport
  | SalesforceItemEmailTemplate
  | SalesforceItemRollup
  | SalesforceItemSweepAutomation
  | SalesforceItemSweepAlert
  | SalesforceItemSweepAssignment
  | SalesforceItemSweepScheduledAssignment
  | SalesforceItemSweepDedupe
  | SalesforceItemSweepMatching
  | SalesforceItemGeneric;

// Type guard functions for runtime type narrowing
export function isFieldConfigurationItem(item: SalesforceItem): item is SalesforceItemField {
  return item.parentType === FIELD;
}

export function isFlowConfigurationItem(item: SalesforceItem): item is SalesforceItemFlow {
  return item.parentType === FLOW || item.parentType === PROCESS_BUILDER_FLOW;
}

export function isRollupConfigurationItem(item: SalesforceItem): item is SalesforceItemRollup {
  return item.parentType === ROLLUP;
}

export function isSweepElementConfigurationItem(
  item: SalesforceItem,
): item is SalesforceItemBaseSweepElement {
  return (
    item.parentType === SWEEP_AUTOMATION ||
    item.parentType === SWEEP_ALERT ||
    item.parentType === SWEEP_ASSIGNMENT ||
    item.parentType === SWEEP_SCHEDULED_ASSIGNMENT ||
    item.parentType === SWEEP_DEDUPE ||
    item.parentType === SWEEP_MATCHING
  );
}

export function isValidationRuleConfigurationItem(
  item: SalesforceItem,
): item is SalesforceItemValidationRule {
  return item.parentType === VALIDATION_RULE;
}

// Type guard to check if item has field-like properties (Field or Rollup)
export function hasFieldProperties(
  item: SalesforceItem,
): item is SalesforceItemField | SalesforceItemRollup {
  return isFieldConfigurationItem(item) || isRollupConfigurationItem(item);
}
